<template>
    <v-group :config="{y: yPos + 1}">
        <v-line note="horizontal line for rows" :config="{...hLineConfig, y: -38}"></v-line>

        <v-group note="row"
                 v-for="(item, index) in items"
                 :key="item.name"
                 :config="{ y: (index * rowHeight) }"
        >
            <BoxListingHover note="edit: line, rib, hem"
                             v-slot="lineSlotProps"
                             @hover="hoverRowItem($event, item)"
                             @dblclick="editRowItem(item)"
            >
                <v-rect note="rectangle for listen hover and click events"
                        :config="{
                            width: 190,
                            height: rowHeight,
                            fill: cellItemFillColor(lineSlotProps.hover, item.type),
                        }"
                ></v-rect>

                <v-text note="line name; width=110"
                        :config="{
                        ...textConfig,
                        x: 10,
                        text: item.name,
                    }"
                ></v-text>
                <v-text note="line length; width=80"
                        :ref="refName(item)"
                        :config="{
                        ...textConfig,
                        x: 120,
                        text: item.lineLength,
                    }"
                ></v-text>
            </BoxListingHover>

            <BoxListingHover note="edit: angle"
                             v-slot="angleSlotProps"
                             :config="{x: 190}"
                             @hover="hoverAngleItem($event, item)"
                             @dblclick="editAngle(item)"
            >
                <v-rect note="rectangle for listen hover and click events"
                        :config="{
                            width: 60,
                            height: rowHeight,
                            fill: cellItemFillColor(angleSlotProps.hover),
                        }"
                ></v-rect>

                <v-text note="angle; width=60"
                        :ref="refName(item)"
                        :config="{
                        ...textConfig,
                        width: 60,
                        align: 'center',
                        text: item.angleSize,
                    }"
                ></v-text>
            </BoxListingHover>

            <v-line note="horizontal line for rows" :config="hLineConfig"></v-line>
            <v-line note="vertical line for columns" :config="vLineConfig(110)"></v-line>
            <v-line note="vertical line for columns" :config="vLineConfig(190)"></v-line>
        </v-group>

    </v-group>
</template>

<script>
    import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
    import BoxListingHover from "./BoxListingHover";

    export default {
        name: "BoxListingCanvas",

        components: {
            BoxListingHover,
        },

        props: [
            'yPos',
            'rowHeight',
            'rowWidth',
            'textColor',
            'lineColor',
        ],

        computed: {
            ...mapState('editor', [
                'points',
            ]),

            ...mapGetters('editor', [
                'trimEditDisabled',
                'getPointById',
                'hems',
                'trimTotalLength',
                'getFootsInFractions',
            ]),

            ...mapGetters('angles', [
                'getAngles',
            ]),

            ...mapGetters('line', [
                'getLinesWithoutFolds',
                'getLineWithRibsStartingLength',
            ]),

            angles() {
                return this.getAngles(this.points);
            },

            lines() {
                return this.getLinesWithoutFolds(this.points);
            },

            items() {
                let linesLength = this.lines.length;

                let items = [];

                let startHem = this.hems[0];
                let endHem = this.hems[1];

                if (startHem) {
                    let item = this.makeItemForHem(startHem);

                    items.push(item);
                }

                for (let i = 0; i < linesLength; i++) {

                    let line = this.lines[i];
                    let angle = this.angles[i];

                    let name = 'Line ' + (i + 1);
                    let lineLength = this.getFootsInFractions(line.lengthInFeet) + '"';
                    let angleSize = (angle && angle.angleSize + '°') || '-';

                    let isLineWithRibs = line.type === "lineWithRibs";

                    if (isLineWithRibs) {
                        name = 'Rib ' + (i + 1);
                        lineLength = this.getFootsInFractions(this.getLineWithRibsStartingLength(line)) + '"';
                    }

                    // item with type='line' use for: line, rib, angle
                    let item = {
                        type: isLineWithRibs ? 'lineWithRibs' : 'line',
                        name,
                        lineLength,
                        angleSize,
                        lineId: line?.id,
                        angleId: angle?.id,
                    };

                    items.push(item);
                }

                if (endHem) {
                    let item = this.makeItemForHem(endHem);

                    items.push(item);
                }

                return items;
            },

            hLineConfig() {
                return {
                    points: [0, this.rowHeight, this.rowWidth, this.rowHeight],
                    stroke: this.lineColor,
                    strokeWidth: 1,
                }
            },

            textConfig() {
                return {
                    y: 10,
                    fontSize: 16,
                    fontFamily: 'Arial, sans-serif',
                    lineHeight: 1.2,
                    fill: this.textColor,
                }
            },

        },

        methods: {
            ...mapMutations('editor', [
                'setHemIsHover',
            ]),

            ...mapMutations('line', [
                'setLineIsHover',
            ]),

            ...mapMutations('angles', [
                'setAngleIsHover',
            ]),

            ...mapActions('editor', [
                'hoverTrimItem',
            ]),

            ...mapActions('editPopup', [
                'openAngleEditPopup',
                'openLineEditPopup',
                'openHemEditPopup',
            ]),

            ...mapActions('inputCell', [
                'setActiveCell',
            ]),

            refName(item) {
                let angleId = item.angleId;

                if (angleId) {
                    return `angle-${angleId}`;
                }

                let id = item.type === 'line' ? item.lineId : item.hemId;

                return `${item.type}-${id}`;
            },

            makeItemForHem(hem) {
                let name = hem.type === 'closeHem' ? 'Close hem' : 'Open hem';
                let lineLength = this.getFootsInFractions(hem.size) + '"';
                let angleSize = '-';

                let item = {
                    type: 'hem',
                    name,
                    lineLength,
                    angleSize,
                    hemId: hem.id,
                };

                return item;
            },

            vLineConfig(x) {
                return {
                    points: [x, 0, x, this.rowHeight],
                    stroke: '#ddd',
                    strokeWidth: 1,
                }
            },

            hoverRowItem(isHover, item) {
                let itemType = item.type;
                let pointId = item.hemId || item.lineId

                this.hoverTrimItem({isHover, itemType, pointId});
            },

            hoverAngleItem(isHover, item) {
                let pointId = item.angleId;

                if (!pointId) return;

                let itemType = 'angel';

                this.hoverTrimItem({isHover, itemType, pointId});
            },

            getItemCellPosition(item) {
                let refs = this.$refs[this.refName(item)];

                let elNode = refs[0].getNode();

                return elNode.getAbsolutePosition();
            },

            editRowItem(item) {
                let position = this.getItemCellPosition(item);

                switch (item.type) {
                    case 'line':
                        this.editLine(item.lineId, position);
                        break;

                    case 'hem':
                        this.editHem(item.hemId, position);
                        break;

                    default:
                        break;
                }

            },

            editLine(lineId, position) {
                let currentLine = this.lines.find(line => line.id === lineId);

                this.setActiveCell({type: 'line', item: currentLine, position});
            },

            editHem(hemId, position) {
                let currentHem = this.getPointById(hemId);

                this.setActiveCell({type: 'hem', item: currentHem, position});
            },

            editAngle(item) {
                const angleId = item.angleId;

                if (!angleId) return;

                let currentAngle = this.angles.find(angle => angle.id === angleId);

                let position = this.getItemCellPosition(item);

                this.setActiveCell({type: 'angle', item: currentAngle, position});
            },

            cellItemFillColor(isHover, itemType) {
                let editDisabled = this.trimEditDisabled || itemType === 'lineWithRibs';

                return isHover ? editDisabled ? '#eee' : '#ccc' : null;
            },

            /*
            fnForConvertHtmlToImage() {
                // if need use html instead of canvas
                let self = this;

                let html = `121212121`;

                const svg = `
                    <svg xmlns="http://www.w3.org/2000/svg" width="300" height="300">
                    <foreignObject width="100%" height="100%">
                        <div xmlns="http://www.w3.org/1999/xhtml">${html}</div>
                    </foreignObject>
                    </svg>`;

                const svgBlob = new Blob( [svg], { type: 'image/svg+xml' } );

                let reader = new FileReader();
                reader.readAsDataURL(svgBlob);

                reader.onload = function() {
                    let src = reader.result;

                    self.$utils.loadImage((img) => {
                        self.imgTest = img;
                    }, src);
                };

            },
            */

        },

    }
</script>

<style>

</style>